import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { CircularGauge } from '@progress/kendo-react-gauges';
import { useLocalization } from '@progress/kendo-react-intl';
import { Loader } from '@progress/kendo-react-indicators';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { AccessToken } from '../../config/AccessToken';

type ConversionsChartProps = {
  firstValue?: number;
  secondValue?: number;
  firstLabel?: string;
  secondLabel?: string;
  firstLabelLoading?: boolean;
  secondLabelLoading?: boolean;
  includeDeviceTotal?: boolean;
};

export const ConversionsChart = (values: ConversionsChartProps = {}) => {

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const [instance, accessTokenRequest] = AccessToken();
  const localizationService = useLocalization();
  const languageId = localStorage.getItem('languageId');
  const [counterDevices, setCounterDevices] = useState<number | null>(null);


  const firstValue = values.firstValue ?? 0
  const secondValue = values.secondValue ?? 0
  const gaugeSize = 'clamp(74px, 20vw, 120px)';
  const valueFontSize = 'clamp(0.82rem, 1.7vw, 1.5rem)';
  const labelFontSize = 'clamp(0.68rem, 1.35vw, 1.25rem)';
  const gaugeItemStyle = {
    flex: '1 1 110px',
    minWidth: '92px',
    maxWidth: '170px',
  };

  const resolvedCounterDevices = Number(counterDevices ?? 0);
  const fallbackMaxFromValues = Math.max(1, firstValue, secondValue, firstValue + secondValue);
  const gaugeScaleMax = values.includeDeviceTotal
    ? Math.max(1, resolvedCounterDevices || fallbackMaxFromValues)
    : 100;

  const colors = [
    {
        from: 0,
        to: firstValue,
        color: '#5FE406'
    },
    {
        from: firstValue,
        to: gaugeScaleMax,
        color: 'grey'
    }
  ];

  const gaugeItemStyle3 = {
    minWidth: '92px',
    maxWidth: '170px',
  };

  const arcOptions = {
    value: firstValue,
    transitions: false,
    colors
  };

  const arcSecondOptions = {
    value: secondValue,
    transitions: false,
  };

  const arcTotalOptions = {
    value: Number(counterDevices ?? 0),
    transitions: false,
  };

  useEffect(() => {
    if (!values.includeDeviceTotal) {
      return;
    }

    const getDeviceCounter = async () => {
      const commonOperations = (accessToken: any) => {
        const headers = { Authorization: `Bearer ${accessToken}`, Language: languageId };

        axios
          .get(`${SERVER_URL}/device/count`, { headers })
          .then((res) => {
            const deviceCount = Number(res?.data?.data?.deviceCount ?? 0);
            setCounterDevices(Number.isFinite(deviceCount) ? deviceCount : 0);
          })
          .catch(() => {
            setCounterDevices(0);
          });
      };

      if (localStorage.getItem('accessToken') != null) {
        const accessToken = localStorage.getItem('accessToken');
        commonOperations(accessToken);
      } else {
        await instance.initialize();
        await instance
          .acquireTokenSilent(accessTokenRequest)
          .then((accessTokenResponse: any) => {
            const accessToken = accessTokenResponse.accessToken;
            commonOperations(accessToken);
          })
          .catch((error: any) => {
            if (error instanceof InteractionRequiredAuthError) {
              instance.acquireTokenRedirect(accessTokenRequest);
            }
            setCounterDevices(0);
          });
      }
    };

    void getDeviceCounter();
  }, [SERVER_URL, accessTokenRequest, instance, languageId, values.includeDeviceTotal]);

  const arcCenterRenderer = () => {
    return (
      <div>
        <h3 style={{fontSize: valueFontSize, fontWeight: 'bold', color:'#198894'}}>
          {values.firstLabelLoading ? (
            <Loader size="small" type="infinite-spinner" themeColor="primary" />
          ) : (
            firstValue
          )}
        </h3>        
      </div>
    );
  };

  const secondArcCenterRenderer = () => {
    return (
      <h3  style={{fontSize: valueFontSize, fontWeight: 'bold', color:'#198894'}}>
        {values.secondLabelLoading ? (
          <Loader size="small" type="infinite-spinner" themeColor="primary" />
        ) : (
          secondValue
        )}
      </h3>
    );
  };

  const totalArcCenterRenderer = () => {
    return (
      <h3 style={{ fontSize: valueFontSize, fontWeight: 'bold', color: '#198894' }}>
        {counterDevices ?? 0}
      </h3>
    );
  };

  

  return (
    <div className="circular-gauge-container" style={{ justifyContent: 'center', gap: 'clamp(4px, 1vw, 10px)', width: '100%' }}>
      <div style={gaugeItemStyle}>
        <CircularGauge 
          {...arcOptions} 
          arcCenterRender={arcCenterRenderer} 
          style={{ width: gaugeSize, height: gaugeSize, margin: '0 auto' }}
          //color='#5FE406'
          scale={{
            startAngle: 90,
            min: 0,
            max: gaugeScaleMax,
          }} 
          />
          <h4
            style={{
              fontSize: labelFontSize,
              color: '#666666',
              lineHeight: 1.2,
              width: gaugeSize,
              textAlign: 'center'
            }}
          >{values.firstLabel ?? localizationService.toLanguageString('dashboard.widget.devices.active', 'NOT_FOUND')}</h4>
          <br />
      </div>

      <div style={gaugeItemStyle}>
        <CircularGauge 
          {...arcSecondOptions} 
          arcCenterRender={secondArcCenterRenderer} 
          style={{ width: gaugeSize, height: gaugeSize, margin: '0 auto' }} 
          color='#FF6358'
          scale={{
            startAngle: 90,
            min: 0,
            max: gaugeScaleMax,
          }} />          
          <h4
            style={{
              fontSize: labelFontSize,
              color: '#666666',
              textAlign: 'center',
              minHeight: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              lineHeight: 1.2,
              width: '120px',
            }}
          >
            <span>
              {values.secondLabel ?? localizationService.toLanguageString("dashboard.widget.devices.inactive", 'NOT_FOUND')}
            </span>
          </h4>
          <br />
        </div>

      {values.includeDeviceTotal ? (
        <div style={gaugeItemStyle3}>
          <CircularGauge
            {...arcTotalOptions}
            arcCenterRender={totalArcCenterRenderer}
            style={{ width: gaugeSize, height: gaugeSize, margin: '0 auto' }}
            color="#2F80ED"
            scale={{
              startAngle: 90,
              min: 0,
              max: gaugeScaleMax,
            }}
          />
          <h4
            style={{
              fontSize: labelFontSize,
              color: '#666666',
              textAlign: 'center',
              lineHeight: 1.2,
              width: '120px',
            }}
          >
            {localizationService.toLanguageString('dashboard.widget.devices.total', 'NOT_FOUND')}
          </h4>
          <br />
        </div>
      ) : null}
      </div>
  );
};

